import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import KanbanView from "@/pages/KanbanView";
import TeamOverview from "@/pages/TeamOverview";
import TasksOverview from "@/pages/TasksOverview";
import VibeMeeting from "@/pages/VibeMeeting";
import ApprovalsPage from "@/pages/ApprovalsPage";
import AIAssistant from "@/pages/AIAssistant";
import AIChat from "@/pages/AIChat";
import Communications from "@/pages/Communications";
import AuthCallback from "@/pages/AuthCallback";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Returning from Google sign-in: AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setUser(response.data);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter user={user} setUser={setUser} />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function AppRouter({ user, setUser }) {
  const location = useLocation(); // read the hash from here (reactive), not window.location
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }
  return (
        <Routes>
          {/* Landing page - public */}
          <Route 
            path="/" 
            element={
              user ? (
                <Navigate to="/dashboard/kanban" replace />
              ) : (
                <LandingPage />
              )
            } 
          />
          
          {/* Login page */}
          <Route 
            path="/login" 
            element={
              user ? (
                <Navigate to="/dashboard/kanban" replace />
              ) : (
                <LoginPage setUser={setUser} />
              )
            } 
          />
          
          {/* Password reset from emailed link - public */}
          <Route path="/reset-password" element={<ResetPasswordPage setUser={setUser} />} />
          
          {/* Dashboard routes - protected */}
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/login" replace />}
          >
            <Route index element={<Navigate to="/dashboard/kanban" replace />} />
            <Route path="kanban" element={<KanbanView user={user} />} />
            <Route path="team" element={<TeamOverview user={user} />} />
            <Route path="tasks" element={<TasksOverview user={user} />} />
            <Route path="meetings" element={<VibeMeeting user={user} />} />
            <Route path="approvals" element={<ApprovalsPage user={user} />} />
            <Route path="ai-assistant" element={<AIAssistant user={user} />} />
            <Route path="chat" element={<AIChat user={user} />} />
            <Route path="communications" element={<Communications user={user} />} />
          </Route>
          
          {/* Catch all - redirect to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

export default App;
